import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../../config/api';
import PageHeader from '../../components/PageHeader';
import {
  fetchTestDb,
  fetchTestPrisma,
  fetchLastUpdated,
  fetchScripts,
  fetchScriptRuns,
  triggerScript,
  timeAgo,
  isActiveStatus,
} from './serverStatusQueries';

async function fetchUncategorizedCount(): Promise<number> {
  const [rasff, alerts] = await Promise.all([
    authFetch(`${API_BASE_URL}/api/rasff-notifications/uncategorized?limit=1`)
      .then(r => (r.ok ? r.json() : { total: 0 })),
    authFetch(`${API_BASE_URL}/api/food-alerts/uncategorized?limit=1`)
      .then(r => (r.ok ? r.json() : { total: 0 })),
  ]);
  return (rasff?.total || 0) + (alerts?.total || 0);
}

interface Subscriber {
  id: number;
  email: string;
  active: boolean;
}

async function fetchSubscriberSummary(): Promise<{ active: number; total: number }> {
  const res = await authFetch(`${API_BASE_URL}/api/notifications/subscribers`);
  if (!res.ok) return { active: 0, total: 0 };
  const subs: Subscriber[] = await res.json();
  return {
    active: subs.filter(s => s.active).length,
    total: subs.length,
  };
}

function HazardMappingCard() {
  const { data: uncategorized } = useQuery(['uncategorized-count'], fetchUncategorizedCount, {
    staleTime: 60_000,
  });

  return (
    <Link to="/admin/hazards" className="admin-hub__card">
      <div className="admin-hub__card-header">
        <h3>Hazard Mapping</h3>
        {uncategorized !== undefined && uncategorized > 0 && (
          <span className="card-badge">{uncategorized} to classify</span>
        )}
      </div>
      <div className="admin-hub__card-body">
        <p>Assign BRC v9 hazard categories to RASFF notifications and food alerts.</p>
        <span className="admin-hub__card-link">Open hazard mapping →</span>
      </div>
    </Link>
  );
}

async function fetchMrlReviewCount(): Promise<number> {
  const res = await authFetch(`${API_BASE_URL}/api/admin/mrl/mappings/summary`);
  if (!res.ok) return 0;
  const data = await res.json() as { total?: number };
  return data.total ?? 0;
}

function MrlMappingCard() {
  const { data: toReview } = useQuery(['mrl-mapping-summary'], fetchMrlReviewCount, {
    staleTime: 60_000,
  });

  return (
    <Link to="/admin/mrl-mappings" className="admin-hub__card">
      <div className="admin-hub__card-header">
        <h3>MRL Mappings</h3>
        {toReview !== undefined && toReview > 0 && (
          <span className="card-badge">{toReview} to review</span>
        )}
      </div>
      <div className="admin-hub__card-body">
        <p>Match US and other non-EU pesticide limits to EU products and residues, and switch registers on or off.</p>
        <span className="admin-hub__card-link">Open MRL mappings →</span>
      </div>
    </Link>
  );
}

function NotificationsCard() {
  const { data: summary } = useQuery(['subscriber-summary'], fetchSubscriberSummary, {
    staleTime: 60_000,
  });

  return (
    <Link to="/admin/notifications" className="admin-hub__card">
      <div className="admin-hub__card-header">
        <h3>Notifications</h3>
        {summary && (
          <span className="card-badge">{summary.active} active</span>
        )}
      </div>
      <div className="admin-hub__card-body">
        <p>Manage digest subscribers and their daily/weekly email preferences.</p>
        <span className="admin-hub__card-link">Open notifications →</span>
      </div>
    </Link>
  );
}

async function fetchHeldSocialCount(): Promise<number> {
  const res = await authFetch(`${API_BASE_URL}/api/social/posts?status=held`);
  if (!res.ok) return 0;
  const data = await res.json() as { ok: boolean; posts?: unknown[] };
  return data.posts?.length ?? 0;
}

function SocialAgentCard() {
  const { data: heldCount } = useQuery(['social-held-count'], fetchHeldSocialCount, {
    staleTime: 60_000,
  });

  return (
    <Link to="/admin/social" className="admin-hub__card">
      <div className="admin-hub__card-header">
        <h3>Social Agent</h3>
        {heldCount !== undefined && heldCount > 0 && (
          <span className="card-badge">{heldCount} to review</span>
        )}
      </div>
      <div className="admin-hub__card-body">
        <p>Review LinkedIn post drafts and triage reader feedback.</p>
        <span className="admin-hub__card-link">Open social agent →</span>
      </div>
    </Link>
  );
}

function SystemStatusCard() {
  const queryClient = useQueryClient();

  const { data: dbStatus, isError: dbError } = useQuery(['testDb'], fetchTestDb, {
    refetchInterval: 30_000,
  });
  const { data: prismaStatus, isError: prismaError } = useQuery(['testPrisma'], fetchTestPrisma, {
    refetchInterval: 30_000,
  });
  const { data: lastUpdatedData } = useQuery(['lastUpdated'], fetchLastUpdated, {
    refetchInterval: 60_000,
  });
  const { data: scripts } = useQuery(['scripts'], fetchScripts);
  const { data: scriptRuns } = useQuery(['scriptRuns'], fetchScriptRuns, {
    refetchInterval: (data) => (data?.some(r => isActiveStatus(r.status)) ? 2000 : false),
  });

  const triggerMut = useMutation(triggerScript, {
    onSuccess: () => queryClient.invalidateQueries(['scriptRuns']),
  });

  const dbOk = !dbError && dbStatus?.ok !== false;
  const prismaOk = !prismaError && prismaStatus?.ok !== false;

  // Derive "worker" health from the most recent successful run_all/update_* entry.
  const lastWorkerRun = lastUpdatedData?.lastRuns?.[0];
  const workerOk = lastWorkerRun?.status === 'success';

  const hasActiveRun = scriptRuns?.some(r => isActiveStatus(r.status));
  const quickScripts = scripts?.filter(s => s.name === 'run_all' || s.name === 'update_rasff') ?? [];

  return (
    <div className="admin-hub__card admin-hub__card--wide">
      <div className="admin-hub__card-header">
        <h3>System Status</h3>
        <Link to="/admin/status" className="admin-hub__card-link admin-hub__card-link--inline">
          Full details →
        </Link>
      </div>
      <div className="admin-hub__card-body">
        <div className="status-pills">
          <div className="status-pill">
            <span className={`status-dot ${dbOk ? 'status-dot--ok' : 'status-dot--err'}`} />
            <span className="status-pill__label">Database</span>
          </div>
          <div className="status-pill">
            <span className={`status-dot ${prismaOk ? 'status-dot--ok' : 'status-dot--err'}`} />
            <span className="status-pill__label">Prisma ORM</span>
          </div>
          <div className="status-pill">
            <span className={`status-dot ${workerOk ? 'status-dot--ok' : 'status-dot--err'}`} />
            <span className="status-pill__label">Worker</span>
          </div>
        </div>

        {lastWorkerRun && (
          <p className="status-summary">
            Last ingestion: <strong>{timeAgo(lastWorkerRun.run_time)}</strong>
            {' · '}
            <span className="status-summary__muted">{lastWorkerRun.script_name}</span>
          </p>
        )}

        {quickScripts.length > 0 && (
          <div className="admin-hub__quick-actions">
            {quickScripts.map(s => (
              <button
                key={s.name}
                className={`btn ${s.name === 'run_all' ? 'btn--primary' : 'btn--secondary'}`}
                onClick={(e) => {
                  e.preventDefault();
                  triggerMut.mutate(s.name);
                }}
                disabled={!!hasActiveRun || triggerMut.isLoading}
                title={s.description}
              >
                {s.label}
              </button>
            ))}
            {hasActiveRun && <span className="admin-hub__running">Running…</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ManageHubPage() {
  return (
    <div className="container admin-hub">
      <PageHeader
        title="Manage"
        tagline="Hazard taxonomy, subscriber lists, and system health."
      />
      <div className="admin-hub__grid">
        <HazardMappingCard />
        <MrlMappingCard />
        <NotificationsCard />
        <SocialAgentCard />
        <SystemStatusCard />
      </div>
    </div>
  );
}
